import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, RefreshCw, Sparkles, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { dropPhoto, loadProducts, Photo } from "@/components/quote/ProductPicker";

const btn = "inline-flex h-9 items-center gap-2 rounded-full px-4 text-sm font-semibold transition-colors disabled:opacity-50";

function PhotoCard({ ph, i, onChanged }) {
  const file = useRef(null);
  const [v, setV] = useState(0);
  const regen = async () => {
    try {
      await api.post(`/settings/catalog/photos/${ph.key}/regenerate`);
      toast.success("Regenerating, this takes about a minute");
      onChanged();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const upload = async (f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post(`/settings/catalog/photos/${ph.key}/upload`, fd);
      dropPhoto(`${ph.key}:${v}`);
      setV(v + 1);
      toast.success("Photo replaced");
      onChanged();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const busy = ["queued", "generating"].includes(ph.status);
  return (
    <div data-testid={`catalog-photo-card-${i}`} className="rounded-xl border border-slate-800 p-2">
      <Photo k={ph.status === "ready" ? ph.key : ""} version={`${v}-${ph.updated_at}`} className="h-32 w-full" testId={`catalog-photo-${ph.key}`} />
      <p className="mt-2 text-xs font-medium text-slate-100">{ph.category} · {ph.color}</p>
      <p data-testid={`catalog-photo-status-${i}`} className={`text-[11px] ${ph.status === "failed" ? "text-red-400" : "text-slate-500"}`}>
        {busy ? "Generating…" : ph.status === "ready" ? (ph.source === "upload" ? "Uploaded" : "AI photo") : ph.status}{ph.error ? `: ${ph.error}` : ""}
      </p>
      <div className="mt-1 flex gap-1">
        <button data-testid={`catalog-photo-regenerate-${i}`} disabled={busy} onClick={regen} title="Regenerate with AI" className="rounded p-1 text-slate-400 hover:text-amber-500 disabled:opacity-40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </button>
        <button data-testid={`catalog-photo-upload-${i}`} onClick={() => file.current?.click()} title="Upload a replacement" className="rounded p-1 text-slate-400 hover:text-amber-500"><Upload className="h-4 w-4" /></button>
        <input ref={file} type="file" accept=".png,.jpg,.jpeg,.webp" hidden onChange={(e) => upload(e.target.files?.[0])} />
      </div>
    </div>
  );
}

export function CatalogSection() {
  const [photos, setPhotos] = useState([]);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const file = useRef(null);
  const load = useCallback(() => {
    api.get("/settings/catalog/photos").then((r) => setPhotos(r.data)).catch(() => {});
    loadProducts(true).then((p) => setCount(p.length)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const working = photos.some((p) => ["queued", "generating"].includes(p.status));
  useEffect(() => {
    if (!working) return undefined;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [working, load]);
  const doImport = async (f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      const { data } = await api.post("/settings/catalog/import", fd);
      toast.success(`Imported ${data.items} products (${data.new} new) · ${data.photos} photo styles`);
      load();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const generate = async () => {
    try {
      const { data } = await api.post("/settings/catalog/photos/generate");
      toast.success(`Generating ${data.queued} photos (about $${data.cost_estimate})`);
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const missing = photos.filter((p) => ["missing", "failed"].includes(p.status)).length;
  return (
    <section data-testid="catalog-section" className="rounded-2xl border border-slate-800 p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold text-slate-50">Viewlux catalog</h2>
          <p className="mt-1 text-sm text-slate-400"><span data-testid="catalog-product-count">{count}</span> products shown on quotes and PDFs. Priced by the category's supplier line.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="catalog-import-button" disabled={busy} onClick={() => file.current?.click()} className={`${btn} border border-slate-700 text-slate-200 hover:border-amber-500`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Import item export (.xls)
          </button>
          <input ref={file} type="file" accept=".xls" hidden onChange={(e) => doImport(e.target.files?.[0])} />
          <button data-testid="catalog-generate-button" disabled={!missing || working} onClick={generate} className={`${btn} bg-amber-500 text-white hover:bg-amber-600`}>
            <Sparkles className="h-4 w-4" /> Generate {missing || ""} missing photos
          </button>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-6">
        {photos.map((ph, i) => <PhotoCard key={ph.key} ph={ph} i={i} onChanged={load} />)}
      </div>
    </section>
  );
}
